// Emisor GLOBAL de ubicación durante la RUTA. Ver CLAUDE.md §7.
// Vive montado en App: el repartidor navega por toda la app sin dejar de transmitir.
//
// NO hay interruptor de "compartir ubicación": el rastreo arranca solo cuando el
// repartidor marca "En ruta" y se apaga cuando cierra su última entrega. La fuente
// de la verdad es el servidor (el estado de las entregas), no el teléfono, así que
// sobrevive a que cierre la app, se quede sin batería o cambie de equipo.
//
// APP NATIVA: Transistorsoft background-geolocation — el servicio NATIVO captura
// y POSTEA las ubicaciones directo a /api/v1/locations/beacon (headers = llave de
// turno), sin depender del WebView ni de MSAL. Inmune al congelamiento de JS de
// Samsung/One UI; con stopOnTerminate:false sobrevive incluso si el sistema mata
// la app. Heartbeat cada 60s cubre al técnico quieto.
//
// PWA (navegador): GPS web cada 30s vía fetch con llave (se pausa con pantalla
// bloqueada — límite del navegador; para eso existe la app).
import { useCallback, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Capacitor } from '@capacitor/core'
import BackgroundGeolocation from '@transistorsoft/capacitor-background-geolocation'
import { ensureShiftKey, postLocation, postShiftEnd, readShiftKey } from '@/services/locations'
import { listDeliveries } from '@/services/deliveries'
import { useGeolocation } from '@/hooks/useGeolocation'
import { useAuth } from '@/hooks/useAuth'
import { MAX_SHIFT_MS, SHIFT_KEY, useShiftStore } from '@/store/shiftStore'
import { trackDecision } from '@/store/tracking'

const isNative = Capacitor.isNativePlatform()

export function ShiftBeacon() {
  const { user } = useAuth()
  const sharing = useShiftStore((s) => s.sharing)
  const registerEndSender = useShiftStore((s) => s.registerEndSender)
  const setError = useShiftStore((s) => s.setError)
  const start = useShiftStore((s) => s.start)
  const stop = useShiftStore((s) => s.stop)
  const { getCurrent } = useGeolocation()

  // ¿Trae ruta abierta? Se reutiliza el listado de entregas — el backend ya acota al
  // repartidor a lo suyo — y se pide size:1 porque solo interesa el total. La clave
  // empieza con 'deliveries' a propósito: al marcar un estado, DeliveryDetail invalida
  // ['deliveries'] y esta consulta se refresca sola, así el rastreo prende al instante.
  const isDriver = user?.role === 'repartidor'
  const { data: route } = useQuery({
    queryKey: ['deliveries', 'ruta-abierta'],
    queryFn: () => listDeliveries({ status: 'en_ruta,en_sitio', size: 1 }),
    enabled: isDriver,
    refetchInterval: 60_000, // red de seguridad (p.ej. si el gerente le reasigna algo)
  })
  const onRoute = (route?.total ?? 0) > 0

  // Ruta abierta más de MAX_SHIFT_MS = el repartidor olvidó cerrarla. Se corta el
  // rastreo y no se vuelve a encender hasta que esa ruta quede cerrada: nadie debe
  // quedar rastreado un día entero por un descuido. Hace falta el ref porque stop()
  // borra justamente la marca de tiempo con la que se detecta el exceso.
  const cutOff = useRef(false)
  const cutOvertime = useCallback(() => {
    cutOff.current = true
    stop()
  }, [stop])

  useEffect(() => {
    const action = trackDecision({ isDriver, onRoute, sharing, cutOff: cutOff.current })
    if (action === 'start') start()
    else if (action === 'stop') stop()
    if (!onRoute) cutOff.current = false // ruta cerrada: se levanta el corte
  }, [isDriver, onRoute, sharing, start, stop])

  const enabled = Boolean(sharing && user)
  const readyDone = useRef(false)

  // Al activarse el turno (app en primer plano), asegura la llave de turno.
  useEffect(() => {
    if (enabled) ensureShiftKey().catch(() => {})
  }, [enabled])

  // Aviso de fin de turno (HTTP con llave, fire-and-forget).
  useEffect(() => {
    registerEndSender(() => {
      postShiftEnd().catch(() => {
        /* si falla, el mapa lo caduca solo por antigüedad */
      })
    })
    return () => registerEndSender(null)
  }, [registerEndSender])

  // Motor NATIVO (Transistorsoft): captura y envío 100% nativos.
  useEffect(() => {
    if (!enabled || !isNative) return
    let cancelled = false

    const boot = async () => {
      try {
        await ensureShiftKey().catch(() => {})
        const k = readShiftKey()
        if (!k) {
          setError('No se pudo obtener la llave de rastreo. Abre la app y vuelve a marcar la entrega como En ruta.')
          return
        }
        const base = window.location.origin

        if (!readyDone.current) {
          // Config DUAL: anidada (nativo v9, app 1.5.0+) + plana (nativo v7, app 1.4.x).
          // ready() devuelve el estado del motor; ver guard de start() abajo.
          // El JS pasa el objeto tal cual al nativo y cada generación lee su forma e
          // ignora la otra; mismos valores en ambas. Quitar la parte plana cuando
          // toda la flota esté en 1.5.0+.
          const legacyFlat = {
            desiredAccuracy: -1, // DESIRED_ACCURACY_HIGH
            distanceFilter: 25,
            stopTimeout: 5,
            locationAuthorizationRequest: 'Always',
            backgroundPermissionRationale: {
              title: 'Permitir ubicación "Todo el tiempo"',
              message:
                'Para compartir tu ubicación durante la ruta con la pantalla apagada, elige "Permitir siempre" en la configuración.',
              positiveAction: 'Ir a configuración',
              negativeAction: 'Cancelar',
            },
            url: `${base}/api/v1/locations/beacon`,
            method: 'POST',
            headers: { 'X-User-Id': k.user_id, 'X-Shift-Key': k.key },
            httpRootProperty: '.',
            locationTemplate:
              '{"lat":<%= latitude %>,"lng":<%= longitude %>,"accuracy":<%= accuracy %>}',
            autoSync: true,
            maxRecordsToPersist: 200,
            stopOnTerminate: false,
            disableMotionActivityUpdates: true,
            startOnBoot: false,
            heartbeatInterval: 60,
            foregroundService: true,
            notification: {
              title: 'Ruta activa · Hotel Depot',
              text: 'Compartiendo tu ubicación mientras entregas.',
            },
          }
          const state = await BackgroundGeolocation.ready({
            ...(legacyFlat as object),
            reset: true,
            // Mitad ANIDADA (plugin v9). Se manda tal cual al nativo; va con `as object`
            // igual que la plana para no depender de qué versión de tipos esté instalada.
            ...({
            geolocation: {
              // -1 = DesiredAccuracy.High (la const existe solo en tipos, no en el JS del plugin)
              desiredAccuracy: -1,
              distanceFilter: 25,
              stopTimeout: 5,
              // Android 11+: el permiso "Permitir siempre" se otorga en Ajustes; este
              // diálogo guía al técnico hasta allá.
              locationAuthorizationRequest: 'Always',
            },
            http: {
              // Envío NATIVO: el servicio postea cada posición él mismo.
              url: `${base}/api/v1/locations/beacon`,
              method: 'POST',
              headers: { 'X-User-Id': k.user_id, 'X-Shift-Key': k.key },
              rootProperty: '.',
              autoSync: true,
            },
            persistence: {
              locationTemplate:
                '{"lat":<%= latitude %>,"lng":<%= longitude %>,"accuracy":<%= accuracy %>}',
              maxRecordsToPersist: 200, // cola offline: si no hay señal, guarda y reintenta
            },
            activity: {
              // Sin API de movimiento: el permiso ACTIVITY_RECOGNITION se bloqueó del
              // manifiesto (política de Apps de Salud de Play — no somos app de salud).
              disableMotionActivityUpdates: true,
            },
            app: {
              stopOnTerminate: false, // si el sistema mata la app, el rastreo sigue
              startOnBoot: false,
              heartbeatInterval: 60, // técnico quieto: latido por minuto
              backgroundPermissionRationale: {
                title: 'Permitir ubicación "Todo el tiempo"',
                message:
                  'Para compartir tu ubicación durante la ruta con la pantalla apagada, elige "Permitir siempre" en la configuración.',
                positiveAction: 'Ir a configuración',
                negativeAction: 'Cancelar',
              },
              notification: {
                title: 'Ruta activa · Hotel Depot',
                text: 'Compartiendo tu ubicación mientras entregas.',
              },
            },
            } as object),
          })
          readyDone.current = true
          // v9 rechaza start() con el motor ya corriendo (p.ej. reabrir la app con
          // turno activo: el servicio sigue vivo por stopOnTerminate:false).
          if (!cancelled && !state.enabled) await BackgroundGeolocation.start()
        } else {
          // Turnos posteriores: refresca URL/llave por si cambiaron.
          await BackgroundGeolocation.setConfig({
            // Dual v9/v7 (ver nota en ready()).
            ...({
              url: `${base}/api/v1/locations/beacon`,
              headers: { 'X-User-Id': k.user_id, 'X-Shift-Key': k.key },
            } as object),
            ...({
              http: {
                url: `${base}/api/v1/locations/beacon`,
                headers: { 'X-User-Id': k.user_id, 'X-Shift-Key': k.key },
              },
            } as object),
          })
          if (!cancelled) await BackgroundGeolocation.start()
        }

        // Turno = rastreo continuo por distancia. Sin el permiso de actividad física
        // (bloqueado por política de Play) el motor no detecta solo que empezaste a
        // moverte — se le ordena explícitamente. API idéntica en plugin 7.x y 9.x.
        if (!cancelled) await BackgroundGeolocation.changePace(true).catch(() => {})
      } catch (e) {
        if (!cancelled)
          setError(
            // Detalle del error visible a propósito (diagnóstico de campo).
            `No se pudo iniciar el GPS en segundo plano: ${(e as Error)?.message ?? String(e)}`,
          )
      }
    }

    const hb = BackgroundGeolocation.onHeartbeat(() => {
      // Latido: fuerza una posición (se postea sola por autoSync/persist).
      BackgroundGeolocation.getCurrentPosition({ samples: 1, persist: true }).catch(() => {})
    })

    boot()
    return () => {
      cancelled = true
      Promise.resolve(hb).then((sub) => sub?.remove?.()).catch(() => {})
      BackgroundGeolocation.stop().catch(() => {})
    }
  }, [enabled, setError])

  // Tick cada 30s — SOLO PWA (en nativo el servicio se encarga).
  useEffect(() => {
    if (!enabled || isNative) return
    let cancelled = false

    const tick = async () => {
      try {
        const started = Date.parse(localStorage.getItem(SHIFT_KEY) ?? '')
        if (!Number.isNaN(started) && Date.now() - started > MAX_SHIFT_MS) {
          cutOvertime()
          return
        }
      } catch {
        /* ignora */
      }
      try {
        const c = await getCurrent()
        if (!cancelled) {
          await postLocation(c.lat, c.lng, c.accuracy).catch(() => {})
          setError(null)
        }
      } catch {
        if (!cancelled)
          setError('No se pudo obtener tu ubicación. Revisa los permisos de GPS.')
      }
    }

    tick()
    const iv = setInterval(tick, 30000)
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      clearInterval(iv)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [enabled, getCurrent, setError, cutOvertime])

  // Corte de seguridad de 16h también en nativo.
  useEffect(() => {
    if (!enabled || !isNative) return
    const iv = setInterval(() => {
      try {
        const started = Date.parse(localStorage.getItem(SHIFT_KEY) ?? '')
        if (!Number.isNaN(started) && Date.now() - started > MAX_SHIFT_MS) cutOvertime()
      } catch {
        /* ignora */
      }
    }, 60_000)
    return () => clearInterval(iv)
  }, [enabled, cutOvertime])

  return null
}
